import {
  Cog8ToothIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import {
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { startCase } from 'lodash';
import React, { useEffect, useId, useRef, useState } from 'react';

import { Checkbox } from '@/components/chariot-ui';
import { NoData } from '@/components/legacy-ui/NoData';
import { adaptColumnsToTanStack } from '@/components/table/columnAdapter';
import { CELL_WIDTHS, ROW_HEIGHT } from '@/components/table/constants';
import { useIndexedRows } from '@/components/table/hooks/useIndexedRows';
import { useResponsiveColumns } from '@/components/table/hooks/useResponsiveColumns';
import { useTableSelectionState } from '@/components/table/hooks/useTableSelectionState';
import { useTableSortingState } from '@/components/table/hooks/useTableSortingState';
import { useTableVirtualizer } from '@/components/table/hooks/useTableVirtualizer';
import { InfiniteScrollIndicator } from '@/components/table/InfiniteScrollIndicator';
import { SortableColumns } from '@/components/table/sortableColumns';
import { SortColumn } from '@/components/table/SortColumn';
import { TableBody } from '@/components/table/TableBody';
import { Column, TableProps } from '@/components/table/types';
import { applyColumnWidth } from '@/components/table/utils/columnWidth';
import { getColumnId } from '@/components/table/utils/getColumnId';
import { Tooltip } from '@/components/Tooltip';
import { useScroll } from '@/hooks';
import {
  HOTKEY_SCOPES,
  useActionHotkey,
  useTableArrowNav,
} from '@/hooks/hotkeys';
import { cn } from '@/utils/classname';

/**
 * CanonicalTable — the in-progress canonical table component (Decision 4).
 *
 * Goal: host the asset table at full feature parity on `@tanstack/react-table`
 * (column/sort/visibility/selection bookkeeping) while preserving every
 * CHARIOT-1377 behavior, then let the other ~37 `Table.tsx` consumers migrate by
 * a one-line import swap. It keeps the exact same `Column<TData>[]` +
 * `TableProps<TData>` public API as `Table.tsx` (Decision 1).
 *
 * Task 5 status: the real body. react-table owns the column model + sorting +
 * row selection bookkeeping (manualSorting, controlled state). The body itself is
 * rendered through the REUSED `TableBody` (path A / B1) — react-table's
 * `columnDef.cell` is never read; `TableBody` renders each cell from the raw
 * `Column<TData>` via its own `TableCellContent`. Virtualization stays on
 * `@tanstack/react-virtual` via the shared `useTableVirtualizer` (Decision 2).
 *
 * Task 6 status: the full `<thead>` — per-column click-to-sort (reused
 * `SortColumn`), `info` tooltip, the layered leading select-all checkbox column
 * (reflects all/none/indeterminate via the selection hook; ported verbatim from
 * `Table.tsx` for pixel parity, NOT shadcn — Decision 4), and the trailing
 * `sortableColumns` gear (reused `SortableColumns`) / `rowActions` column. The
 * select-all + checkbox column are LAYERED (not react-table columns) per
 * Decision 2. The sticky-without-fixedWidth dev warning is ported verbatim
 * (`Table.tsx:100-112`).
 *
 * Task 7 status: responsive integration (Decision 3). `useResponsiveColumns`
 * runs UNCHANGED as the single authority (same reservedWidth calc as Table.tsx);
 * its `visibleColumns` is projected into react-table's `columnVisibility` keyed
 * by the shared `getColumnId` (B2), so `getVisibleLeafColumns()` — and the
 * leaf→raw remap below — honor the engine's decision automatically. `isResponsive`
 * drives `minTableWidth` + `applyColumnWidth({responsiveMode})` exactly as
 * Table.tsx. No `columnOrder` (CG1 — the incoming `columns` array IS the order).
 *
 * Deferred to later tasks (NOT implemented here):
 *  - Task 8: keyboard nav (Alt+F/U/D, arrows), infinite scroll, pending dimming
 *    set-points. `inspecting` is initialized + threaded here, but its set-points
 *    (handleRowClick / arrow nav) land in Task 8.
 */
export function CanonicalTable<TData>(props: TableProps<TData>) {
  const {
    tableClassName,
    columns,
    data: rawData,
    selection,
    status,
    sort,
    error,
    name: tableName,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching = false,
    rowActions,
    groupBy,
    onRowClick,
    loadingRowCount = 25,
    isTableView = false,
    rowClassName,
    noData,
    rowHeight = ROW_HEIGHT,
    keyboardActions,
    keyboardNavigation,
    sortableColumns,
    infiniteScroll,
    maxHeight,
    minHeight,
    getRowProps,
    responsive = true,
  } = props;

  // Infinite-scroll config with defaults (ported verbatim from Table.tsx:115-118).
  const infiniteScrollConfig = {
    minItemsThreshold: infiniteScroll?.minItemsThreshold ?? 10,
    disabled: infiniteScroll?.disabled ?? false,
  };

  const tableId = useId();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const parentRef = useRef<HTMLElement | null>(null);

  // Sticky-without-fixedWidth dev warning (ported verbatim from
  // Table.tsx:98-112). Sticky columns need fixedWidth or shadow/alignment break.
  const warnedKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    for (const col of columns) {
      if (col.sticky && !col.fixedWidth) {
        const key = `${tableName}:${String(col.id)}`;
        if (warnedKeysRef.current.has(key)) continue;
        warnedKeysRef.current.add(key);
        console.warn(
          `Table "${tableName}": column "${String(col.id)}" uses sticky="${col.sticky}" ` +
            `without fixedWidth. Sticky columns must declare fixedWidth or shadow/alignment will break.`
        );
      }
    }
  }, [columns, tableName]);

  // Local inspecting state (M3) — ported from Table.tsx:120. NOT a prop. Set by
  // handleRowClick (normal-row branch) and arrow-key nav (Task 8); read by
  // TableBody for row highlight and by the keyboard-actions listener.
  const [inspecting, setInspecting] = useState<number | null>(null);

  // Hover state (lifted from TableBody — CHARIOT-1377 Bug 2). Reused TableBody
  // takes these so hover paints background only, never z-index.
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  const [expandedGroups, setExpandedGroups] = useState(
    groupBy?.map(group => group.label) || []
  );

  const { indexedData, groupedData } = useIndexedRows(rawData, {
    tableName,
    groupBy,
    expandedGroups,
    status,
  });

  useEffect(() => {
    if (isTableView) {
      parentRef.current = document.getElementById('body');
    } else {
      parentRef.current =
        scrollableRef.current || document.getElementById(tableId);
    }
  }, [tableId, isTableView]);

  // Infinite scroll: fetch the next page when the user scrolls near the bottom
  // (ported verbatim from Table.tsx:183). useScroll only fires fetchNextPage when
  // hasNextPage !== false, so there is no "dead zone" at the end of the last page.
  useScroll(parentRef, fetchNextPage, hasNextPage);

  const enableCheckbox = !selection?.hide;
  // Whether the select-all header checkbox shows (Table.tsx:247).
  const showSelectAllCheckbox = Boolean(selection?.selectAll ?? true);
  const isLoading = status === 'pending' || (isFetching && !isFetchingNextPage);

  // ---- responsive engine (Decision 3) ---------------------------------------
  // `useResponsiveColumns` is the SINGLE authority for which columns show; it is
  // called UNCHANGED, with the SAME reservedWidth calc as Table.tsx:144-146
  // (checkbox + actions). react-table only PROJECTS its decision (no second
  // visibility authority). The reserved width accounts for the layered leading
  // checkbox column + the trailing gear/actions column (both LAYERED, not
  // react-table columns — Decision 2), so the engine's available-width math
  // matches what the table actually renders.
  const reservedWidth =
    (!selection?.hide ? CELL_WIDTHS.checkbox.fixedWidth : 0) +
    (rowActions || sortableColumns ? CELL_WIDTHS.actions.fixedWidth : 0);

  const { visibleColumns, isResponsive } = useResponsiveColumns(
    columns,
    tableContainerRef,
    {
      enabled: responsive,
      reservedWidth,
    }
  );

  // Project the engine's `visibleColumns` into react-table's `columnVisibility`,
  // keyed by the SHARED getColumnId (B2). Because both this projection AND the
  // adapter's ColumnDef.id route through getColumnId, these keys are EXACTLY
  // react-table's leaf ids — so getVisibleLeafColumns() honors the projection
  // and the leaf→raw remap below reflects it automatically. Using different id
  // schemes would make the projection keys miss the leaf ids and responsive
  // hiding would silently no-op (every column always visible).
  const columnVisibility: VisibilityState = {};
  columns.forEach((col, index) => {
    columnVisibility[getColumnId(col, index)] = visibleColumns.includes(col);
  });

  // ---- react-table: column model + sorting + visibility + selection ---------
  // No getSortedRowModel (manualSorting — server sort). columnVisibility is a
  // CONTROLLED projection of the responsive engine's output; onColumnVisibility-
  // Change is a no-op because the engine (not react-table) owns the decision —
  // visibility only ever changes via the engine's ResizeObserver, mirroring how
  // sorting is controlled by the parent. No columnOrder (CG1 — the incoming
  // `columns` array IS the display order; adding columnOrder would create a
  // second ordering authority). react-table still gets the FULL adapted column
  // set so ids/identity stay stable; only columnVisibility hides.
  const { sortingState, onSortingChange } = useTableSortingState(sort);
  const {
    rowSelectionState,
    selectedRows,
    toggleRow,
    toggleSelectAll,
    isAllSelected,
    isIndeterminate,
  } = useTableSelectionState(selection, indexedData);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table's useReactTable returns non-memoizable functions (expected; matches useTableVirtualizer.ts and SelectionTable/MultiSelectTable precedent).
  const table = useReactTable<TData>({
    data: indexedData,
    columns: adaptColumnsToTanStack(columns),
    getRowId: row => (row as { _key: string })._key,
    manualSorting: true,
    enableRowSelection: true,
    state: {
      sorting: sortingState,
      rowSelection: rowSelectionState,
      columnVisibility,
    },
    onSortingChange,
    onColumnVisibilityChange: () => {
      // No-op: the responsive engine (useResponsiveColumns) is the sole owner of
      // visibility; react-table only projects it (Decision 3, single-owner rule).
    },
    getCoreRowModel: getCoreRowModel(),
  });

  // ---- leaf → raw Column<TData> remap (CG2) ---------------------------------
  // TableBody needs raw `Column<TData>` (it reads col.sticky/col.className/
  // applyColumnWidth(col)), NOT ColumnDef. Map each visible leaf column back to
  // its raw Column via the SHARED getColumnId map (Decision 3 / B2). Because
  // columnVisibility (Task 7) keys through the same getColumnId,
  // getVisibleLeafColumns() already reflects the responsive engine's decision —
  // this remap needs no responsive-specific logic.
  const rawColumnsById = new Map<string, Column<TData>>();
  columns.forEach((col, index) => {
    rawColumnsById.set(getColumnId(col, index), col);
  });
  const columnsToRender: Column<TData>[] = table
    .getVisibleLeafColumns()
    .map(leaf => rawColumnsById.get(leaf.id))
    .filter((col): col is Column<TData> => col !== undefined);

  const columnStyles = columnsToRender.map(column =>
    applyColumnWidth(column, {
      tableLayoutFixed: true,
      responsiveMode: isResponsive,
    })
  );

  // Minimum table width for responsive mode (ported from Table.tsx:250-276): the
  // sum of every rendered column's min/fixed width plus the layered checkbox +
  // actions columns. Applied as a min-width on the table so columns never
  // compress below their minWidth. `undefined` (no min) when not responsive.
  const minTableWidth = !isResponsive
    ? undefined
    : columnsToRender.reduce((sum, col) => {
        const width =
          col.fixedWidth || col.isExpandable?.minWidth || col.minWidth || 100;
        return sum + width;
      }, 0) +
      (enableCheckbox ? CELL_WIDTHS.checkbox.fixedWidth : 0) +
      (rowActions || sortableColumns ? CELL_WIDTHS.actions.fixedWidth : 0);

  // Virtualizer + spacer geometry (CHARIOT-1377 Bug 3 + 4) via the shared hook.
  // The per-row `ref={el => virtualizer.measureElement(el)}` wiring (in tbody
  // below) MUST stay at this call site — without it the ±2px zoom snap never
  // runs and Bug 3 regresses to estimateSize-only (R2).
  const { virtualizer, items, before, after } = useTableVirtualizer({
    parentRef,
    rowHeight,
    loadingRowCount,
    isLoading,
    hasNextPage,
    dataLength: groupedData.length,
  });

  // Group expand/collapse (ported verbatim from Table.tsx:292-302). TableBody's
  // colgroup row calls this with the group label; add/remove it from the set.
  function toggleExpandedGroup(group?: string) {
    if (group) {
      setExpandedGroups(expandedGroups => {
        if (expandedGroups.includes(group)) {
          return expandedGroups.filter(g => g !== group);
        }

        return [...expandedGroups, group];
      });
    }
  }

  // ---- keyboard actions: Alt+F/U/D (routed through useActionHotkey) -----------
  // Shared hotkey hook. react-hotkeys-hook skips input/textarea/select/
  // contentEditable by default (enableOnFormTags false), replacing the old
  // hand-rolled `isTyping` guard. Reads the rows to act on by `_key` (selected
  // rows) or by index (the single inspecting row), then fires onKeyboardAction
  // when the pressed code matches.
  //
  // The codes (always `Key<Letter>`) map to react-hotkeys-hook key strings:
  // `KeyF` -> `f`, plus an `alt+` prefix when the consumer sets `altKey: true`.
  // NOTE: when `altKey` is falsy, the old handler fired regardless of the Alt
  // modifier (`isAltKey = altKey ? event.altKey : true`). Binding the plain
  // letter reproduces that "plain key" action and is the intended behavior — we
  // deliberately do NOT also bind the `alt+` variant.
  const hotkeyKeys = keyboardActions
    ? keyboardActions.codes.map(code => {
        const letter = code.replace(/^Key/, '').toLowerCase();
        return keyboardActions.altKey ? `alt+${letter}` : letter;
      })
    : [];

  useActionHotkey(
    hotkeyKeys,
    (event: KeyboardEvent) => {
      if (!keyboardActions) return;

      // Focus-scope ONLY the overlay (in-drawer) case. Multiple <Vulnerabilities inDrawer>
      // can mount at once (TechnologyOsint expanded-repo accordions), all on the
      // never-suppressed `overlay` scope — without this, one keystroke fans out to every
      // mounted in-drawer table. The global /vulnerabilities table keeps firing without
      // focus (it's suppressed instead by HotkeyScopeSync disabling `global` under a drawer).
      const focusScoped =
        (keyboardActions?.scope ?? HOTKEY_SCOPES.global) ===
        HOTKEY_SCOPES.overlay;
      if (
        focusScoped &&
        !scrollableRef.current?.contains(document.activeElement)
      ) {
        return;
      }

      const { codes, onKeyboardAction } = keyboardActions;

      // Handle selected rows (by key) or inspecting row (by index).
      let data: TData[] = [];

      if (selectedRows.length > 0) {
        // Look up selected rows by their _key.
        data = selectedRows
          .map(key => groupedData.find(row => row._key === key))
          .filter(Boolean) as TData[];
      } else if (inspecting !== null) {
        // For inspecting, use the index to look up in rawData.
        const inspectedRow = rawData[inspecting];
        if (inspectedRow) {
          data = [inspectedRow];
        }
      }

      if (codes?.length > 0 && data.length > 0 && codes.includes(event.code)) {
        onKeyboardAction(event.code, data);
      }
    },
    {
      enabled: Boolean(keyboardActions) && (keyboardActions?.enabled ?? true),
      // Scope the row-action hotkeys. Default `global` (suppressed under an open
      // overlay/drawer). An in-drawer table opts into `overlay` via
      // `keyboardActions.scope` so the table the user is viewing keeps its
      // actions while the background page table stays `global`/suppressed.
      scope: keyboardActions?.scope ?? HOTKEY_SCOPES.global,
      // deps mirror the old effect's array for parity. In react-hotkeys-hook v5
      // the callback is held in a ref (always latest), so these don't drive
      // re-registration — they're passed for documentation/parity, not correctness.
      deps: [selectedRows, inspecting, rawData, groupedData, keyboardActions],
    }
  );

  // ---- arrow-key row navigation (focus-scoped, ENG-43) -----------------------
  // Migrated off the legacy global keyboard bus (`keyboardListeners` on
  // `useGlobalState`) onto react-hotkeys-hook with focus/element scoping.
  // `arrowNavRef` is attached to the scroll container below; the binding fires
  // ONLY while focus is inside that container, so on a multi-table page arrows
  // navigate the FOCUSED table instead of whichever table registered last on
  // the old global bus. Index math is preserved verbatim (ArrowUp is a no-op
  // until a row is inspected). Focus is acquired on row-click (handleRowClick).
  // BEHAVIOR CHANGE: arrows now require the table to be focused first — see the
  // useTableArrowNav doc.
  // F3: index spaces. `inspecting` STATE is rawData-space (a row's `_idx`) — set
  // by handleRowClick and compared against `_idx` for the row highlight. The
  // arrow MATH, however, walks `groupedData` POSITIONS (itemCount =
  // groupedData.length), which interleaves injected `colgroup` header rows when
  // groupBy is on. So we translate `inspecting` (rawData `_idx`) → its
  // groupedData position before feeding the hook, and translate the returned
  // position → the underlying row's `_idx` the SAME way the click path resolves
  // it (`groupedData[pos]._idx`), instead of the old `rawData[nextIndex]` which
  // aliased a groupedData-space index into rawData and opened the WRONG entity
  // (and, past the last row, `undefined`).
  const inspectingPos =
    inspecting === null
      ? null
      : (() => {
          const p = groupedData.findIndex(
            row => row._type !== 'colgroup' && Number(row._idx) === inspecting
          );
          return p === -1 ? null : p;
        })();
  const arrowNavRef = useTableArrowNav<HTMLDivElement>({
    enabled: Boolean(keyboardNavigation) && groupedData.length > 0,
    itemCount: groupedData.length,
    inspecting: inspectingPos,
    onNavigate: (nextPos: number, direction: 'up' | 'down') => {
      // `nextPos` is a groupedData position; advance past any colgroup header
      // rows (wrapping) so we land on a real data row.
      const len = groupedData.length;
      const step = direction === 'up' ? -1 : 1;
      let pos = nextPos;
      for (let i = 0; i < len; i++) {
        if (groupedData[pos]?._type !== 'colgroup') break;
        pos = (pos + step + len) % len;
      }
      const row = groupedData[pos];
      if (!row || row._type === 'colgroup') return; // all-headers guard
      const realIdx = Number(row._idx);
      virtualizer.scrollToIndex(pos, { align: 'center' });
      setInspecting(realIdx);
      onRowClick?.(rawData[realIdx], realIdx);
    },
  });

  // ---- row click: checkbox vs normal-row vs group-header --------------------
  // Ported from Table.tsx:379-465. `idx` is the clicked row's index as supplied
  // by TableBody (rawData `_idx` for normal rows, virtualRow.index for group
  // headers). What counts as a "checkbox click" is `target.closest('[data-
  // checkbox]')` — identical to Table.tsx.
  //
  // DIVERGENCE FROM Table.tsx (intentional, per Task 8 spec): Table.tsx's
  // group-header checkbox does CHILD-KEY BULK SELECTION inline via its own
  // setSelectedRows; the Task-4 selection hook exposes only toggleRow (single
  // key) / toggleSelectAll, so bulk child-select cannot be expressed without
  // extending that hook (out of Task 8 scope — Task 8 touches ONLY this file).
  // Per the Task 8 contract the group-header branch routes to toggleExpandedGroup
  // instead. This path is UNREACHABLE for the asset table (AssetTable passes no
  // groupBy), so asset parity is unaffected; full group-select parity for future
  // grouped consumers is a documented follow-up on the selection hook.
  function handleRowClick(
    idx: number,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) {
    // Focus the scroll container so the focus-scoped arrow-key navigation
    // (useTableArrowNav) fires for THIS table after any row interaction.
    scrollableRef.current?.focus({ preventScroll: true });

    const target = event.target as HTMLElement;
    const clickedCheckbox = target?.closest?.('[data-checkbox]');

    if (enableCheckbox && clickedCheckbox) {
      const rowData = groupedData[idx];

      // Group-header checkbox → toggle the group's expand/collapse (Task 8 spec).
      // See DIVERGENCE note above.
      if (rowData && rowData._type === 'colgroup') {
        toggleExpandedGroup(rowData._label);
        return;
      }

      // Normal-row checkbox → simple single-key toggle (matches Table.tsx:436-446
      // via the selection hook's toggleRow).
      const rowKey = rowData?._key || idx.toString();
      toggleRow(rowKey);
    } else {
      // Normal row click → inspect + open drawer (matches Table.tsx:451-452).
      setInspecting(Number(idx));
      onRowClick?.(rawData[Number(idx)], Number(idx), event);
    }
  }

  // ---- empty / error / skeleton state branching (ported from Table.tsx) -----
  const isError = status === 'error';
  // Don't show empty state while fetching with keepPreviousData: isFetching=true
  // with status='success' and rawData=[] would otherwise blank the table.
  const isEmpty = status === 'success' && rawData.length === 0 && !isFetching;
  const isLoadingOrHaveData = !isError && !isEmpty;
  const columnsLength =
    columnsToRender.length +
    (enableCheckbox ? 1 : 0) +
    (rowActions || sortableColumns ? 1 : 0);

  return (
    <div
      data-testid={`table-${tableName}`}
      id={tableId}
      className={cn(!isTableView && 'h-full pb-2')}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Height-constrained container for ALL content states */}
      <div
        className={cn(
          'flex flex-col',
          !isTableView && 'flex-1 overflow-hidden'
        )}
        style={{
          ...(maxHeight && { maxHeight: `${maxHeight}px` }),
          ...(minHeight && { minHeight: `${minHeight}px` }),
        }}
      >
        {isError && tableName !== 'assets' && tableName !== 'agents' && (
          <NoData
            title={`Unable to retrieve your ${tableName}`}
            description={
              error && error.message ? error.message : 'Something went wrong!'
            }
            icon={
              <ExclamationCircleIcon className="text-chariot-status-critical size-20" />
            }
          />
        )}
        {isEmpty ||
        (isError && (tableName === 'assets' || tableName === 'agents')) ? (
          <NoData
            title={noData?.title || `No ${startCase(tableName)} found`}
            description={noData?.description}
            icon={noData?.icon}
            styleType={noData?.styleType}
            action={noData?.action}
          />
        ) : null}
        <div
          ref={el => {
            scrollableRef.current = el;
            tableContainerRef.current = el;
            // Element-scope the arrow-key navigation to this container
            // (focus-gated). react-hotkeys-hook's ref returns null on unmount.
            arrowNavRef(el);
          }}
          // Programmatically focusable so focus-scoped arrow-nav
          // (useTableArrowNav) can fire; focus is acquired on row-click via
          // scrollableRef.current?.focus(). tabIndex={-1} keeps .focus()
          // working while removing this unlabeled container from the
          // sequential Tab order (a sighted keyboard user otherwise lands on
          // an invisible focus:outline-none stop). outline-none avoids a focus
          // ring around the whole table.
          tabIndex={-1}
          className={cn(
            'focus:outline-none',
            !isTableView && 'flex-1 overflow-auto',
            !isLoadingOrHaveData && 'hidden'
          )}
        >
          <table
            tabIndex={-1}
            data-count={indexedData.length}
            className={cn(
              'relative w-full border-collapse overflow-visible focus:outline-hidden',
              `border-0 bg-transparent [&_th>div:not([data-sortable])]:border-0 [&_thead_tr]:bg-transparent`,
              tableClassName
            )}
            style={{
              tableLayout: 'fixed',
              ...(minTableWidth && { minWidth: `${minTableWidth}px` }),
            }}
          >
            <colgroup>
              {enableCheckbox && (
                <col
                  style={{
                    width: `${CELL_WIDTHS.checkbox.fixedWidth}px`,
                    minWidth: `${CELL_WIDTHS.checkbox.minWidth}px`,
                    maxWidth: `${CELL_WIDTHS.checkbox.maxWidth}px`,
                  }}
                />
              )}
              {columnStyles.map((style, idx) => (
                <col key={idx} style={style} />
              ))}
              {(rowActions || sortableColumns) && (
                <col
                  style={{
                    width: `${CELL_WIDTHS.actions.fixedWidth}px`,
                    minWidth: `${CELL_WIDTHS.actions.minWidth}px`,
                    maxWidth: `${CELL_WIDTHS.actions.maxWidth}px`,
                  }}
                />
              )}
            </colgroup>
            {/* Full header (Task 6), ported from Table.tsx:729-853. The leading
                select-all checkbox column and the trailing gear/actions column
                are LAYERED (not react-table columns) per Decision 2. The select-
                all checkbox markup is the chariot-ui Checkbox ported verbatim for
                pixel parity (shadcn migration is a documented follow-up). */}
            <thead className={'sticky z-20'} style={{ top: 0 }}>
              <tr className="relative overflow-visible">
                {enableCheckbox && (
                  <th className="bg-chariot-background-primary relative py-3 pr-1 pl-2 text-center text-sm font-semibold text-nowrap [&>span]:flex [&>span]:w-full [&>span]:justify-center">
                    <div className="th-top-border border-default absolute top-0 left-0 w-full border-b border-solid" />
                    <div className="th-bottom-border bg-chariot-stroke absolute bottom-0 left-0 h-[2px] w-full" />
                    {showSelectAllCheckbox && (
                      <label className={'cursor-pointer'}>
                        <input
                          type="checkbox"
                          ref={el => {
                            // Native indeterminate is a property, not an
                            // attribute; reflect the selection hook's tri-state.
                            if (el) el.indeterminate = isIndeterminate;
                          }}
                          onChange={toggleSelectAll}
                          className="hidden"
                          checked={isAllSelected}
                        />
                        <Checkbox isChecked={isAllSelected} />
                      </label>
                    )}
                  </th>
                )}
                {columnsToRender.map((column, colIdx) => {
                  const sorting =
                    column.sorting === undefined ? true : column.sorting;
                  const allowSorting = sort && sorting;

                  // Click-to-sort: toggle direction on the current column, else
                  // sort a new column descending (Table.tsx:768-785).
                  const handleHeaderClick = () => {
                    if (!allowSorting) return;
                    const isCurrentColumn = column.id === sort.orderBy;
                    if (isCurrentColumn) {
                      sort.onSort({
                        orderBy: column.id as keyof TData,
                        descending: !sort.descending,
                      });
                    } else {
                      sort.onSort({
                        orderBy: column.id as keyof TData,
                        descending: true,
                      });
                    }
                  };

                  const stickyHeaderClass =
                    column.sticky === 'right'
                      ? 'sticky top-0 right-0 bg-chariot-background-primary'
                      : column.sticky === 'left'
                        ? 'sticky top-0 left-0 bg-chariot-background-primary'
                        : '';

                  return (
                    <th
                      key={getColumnId(column, colIdx)}
                      onClick={allowSorting ? handleHeaderClick : undefined}
                      className={cn(
                        'bg-chariot-background-primary relative py-3 pr-1 pl-2 text-left text-sm font-semibold text-nowrap',
                        column.align === 'center' &&
                          'pr-3 text-center [&>span]:flex [&>span]:w-full [&>span]:justify-center',
                        column.align === 'right' &&
                          'text-right [&>span]:flex [&>span]:w-full [&>span]:justify-end',
                        column.className,
                        sort && 'group hover:cursor-pointer',
                        stickyHeaderClass
                      )}
                    >
                      <div className="th-top-border border-default absolute top-0 left-0 w-full border-b border-solid" />
                      <div className="th-bottom-border bg-chariot-stroke absolute bottom-0 left-0 h-[2px] w-full" />
                      <span className={'flex items-center gap-2'}>
                        {column.label}
                        {column.info && (
                          <Tooltip title={column.info}>
                            <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0 cursor-pointer stroke-2" />
                          </Tooltip>
                        )}
                        {allowSorting && (
                          <SortColumn column={column} sort={sort} />
                        )}
                      </span>
                    </th>
                  );
                })}
                {(rowActions || sortableColumns) && (
                  <th className="bg-chariot-background-primary relative py-3 pr-2 pl-2 text-left text-sm font-semibold text-nowrap">
                    <div className="th-top-border border-default absolute top-0 left-0 w-full border-b border-solid" />
                    <div className="th-bottom-border bg-chariot-stroke absolute bottom-0 left-0 h-[2px] w-full" />
                    {sortableColumns && (
                      <div data-sortable className="flex justify-center">
                        <SortableColumns
                          variant="ghost"
                          placeholder=""
                          defaultConfig={sortableColumns.defaultConfig}
                          position="right"
                          activeColumns={sortableColumns.activeColumns}
                          onChangeActiveColumns={
                            sortableColumns.setActiveColumns
                          }
                          icon={<Cog8ToothIcon className="size-6" />}
                        />
                      </div>
                    )}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {before > 0 && (
                <tr>
                  <td colSpan={columnsLength} style={{ height: before }} />
                </tr>
              )}
              {items.map((virtualRow, rowIndex) => {
                const item = groupedData[virtualRow.index];

                // Buffer row (beyond actual data) — invisible spacer (Bug 4).
                if (!item && virtualRow.index >= groupedData.length) {
                  return (
                    <tr
                      key={`buffer-${virtualRow.index}-${groupedData.length}`}
                      data-buffer-row
                      aria-hidden="true"
                      style={{ height: `${rowHeight}px` }}
                    >
                      <td colSpan={columnsLength} style={{ padding: 0 }} />
                    </tr>
                  );
                }

                if (!item) return null;

                const isChecked = item._key
                  ? rowSelectionState[item._key] === true
                  : false;
                const isLastRow = virtualRow.index === groupedData.length - 1;

                return (
                  <TableBody
                    key={item._key}
                    ref={el => {
                      rowRefs.current[virtualRow.index] = el;
                      // Notify virtualizer to measure this element (R2 — the
                      // ±2px zoom snap lives in measureElement).
                      if (el) {
                        virtualizer.measureElement(el);
                      }
                    }}
                    virtualRow={virtualRow}
                    isChecked={isChecked}
                    isLastRow={isLastRow}
                    rowClassName={rowClassName}
                    isClickable={Boolean(onRowClick)}
                    columns={columnsToRender}
                    selectedRows={Object.keys(rowSelectionState)}
                    data={groupedData}
                    rowActions={rowActions}
                    rowData={item}
                    rowIndex={rowIndex}
                    handleRowClick={handleRowClick}
                    enableCheckbox={enableCheckbox}
                    isLoading={isLoading}
                    expandedGroups={expandedGroups}
                    toggleExpandedGroup={toggleExpandedGroup}
                    groupBy={groupBy}
                    sort={sort}
                    inspecting={inspecting}
                    rowHeight={rowHeight}
                    tabIndex={virtualRow.index === inspecting ? 0 : -1}
                    hoveredRowIndex={hoveredRowIndex}
                    setHoveredRowIndex={setHoveredRowIndex}
                    hasSortableColumn={!!sortableColumns}
                    getRowProps={getRowProps}
                  />
                );
              })}
              {after > 0 && (
                <tr>
                  <td colSpan={columnsLength} style={{ height: after }} />
                </tr>
              )}
            </tbody>
          </table>

          {/* Unified infinite scroll indicator — shows loading or scroll hint
              (ported verbatim from Table.tsx:940-948). Lives inside the
              scrollable container so its sticky positioning works; visibility is
              gated internally on (hasMore || isLoading) && itemCount >= threshold. */}
          {!infiniteScrollConfig.disabled && (
            <InfiniteScrollIndicator
              isLoading={Boolean(isFetchingNextPage)}
              hasMore={hasNextPage === true}
              itemCount={rawData?.length || 0}
              minItemsThreshold={infiniteScrollConfig.minItemsThreshold}
            />
          )}
        </div>
      </div>
    </div>
  );
}
